import { A11yNode, GenerateA11ySnapshotOptions } from './types/window';

const isA11yHidden = (el: Element | null): boolean => {
  for (let node = el; node; node = node.parentElement) {
    if (node.hasAttribute('hidden')) return true;
    if ((node as HTMLElement).inert) return true;
    const ariaHidden = node.getAttribute('aria-hidden');
    if (ariaHidden === 'true') return true;
    const style = window.getComputedStyle(node);
    if (style.display === 'none') return true;
    if (style.visibility === 'hidden' || style.visibility === 'collapse') return true;
  }
  return false;
};

export function generateA11ySnapshot(opts?: GenerateA11ySnapshotOptions): A11yNode {
  const isElement = (node: Element | Document | undefined | null): node is Element =>
    !!node && typeof node === 'object' && node.nodeType === Node.ELEMENT_NODE;
  const isDocument = (node: Element | Document | undefined | null): node is Document =>
    !!node && typeof node === 'object' && node.nodeType === Node.DOCUMENT_NODE;

  const root = opts?.root;

  let rootNode: Element;

  if (isElement(root)) {
    rootNode = root;
  } else if (isDocument(root)) {
    rootNode = root.body;
  } else {
    rootNode = document.body;
  }

  const stopAtSelector = opts?.stopAtSelector || '';
  const maxDepth = typeof opts?.maxDepth === 'number' ? opts.maxDepth : Infinity;

  let excludeSelectors: string[] = [];

  if (typeof opts?.exclude === 'string') {
    excludeSelectors = [opts.exclude];
  } else if (Array.isArray(opts?.exclude)) {
    excludeSelectors = opts.exclude;
  }

  const excludeCombined = excludeSelectors.length ? `:is(${excludeSelectors.join(',')})` : '';
  const excludeMode: 'subtree' | 'strip-node' = opts?.excludeMode === 'strip-node' ? 'strip-node' : 'subtree';

  const shouldExclude = (el: Element) => !!excludeCombined && (el.matches?.(excludeCombined) ?? false);

  const elementsToExtract = [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'a',
    'button',
    'input',
    'select',
    'textarea',
    'img',
    'nav',
    'main',
    'aside',
    'article',
    'section',
    'header',
    'footer',
    'ul',
    'ol',
    'li',
    'dl',
    'dt',
    'dd',
    'table',
    'caption',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
    '[role]',
    '[aria-label]',
    '[aria-labelledby]',
    '[data-testid]'
  ].join(',');

  // Only query within rootNode's subtree
  const rawList = Array.from(rootNode.querySelectorAll(elementsToExtract));
  const elementList = rawList.filter((e) => !isA11yHidden(e));
  const elements = new Set<Element>(elementList);

  // Add rootNode itself to elements set if it matches the selector
  if (rootNode.matches?.(elementsToExtract)) {
    elements.add(rootNode);
  }

  const textByParent = new Map<Element, string[]>(elementList.map((e) => [e, []]));
  // Also track text for rootNode
  textByParent.set(rootNode, []);

  // Collect visible text nodes and attach to nearest tracked element
  {
    const treeWalker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT);

    for (let node = treeWalker.nextNode(); node; node = treeWalker.nextNode()) {
      const parent = (node as Text).parentElement;
      if (isA11yHidden(parent)) continue;

      const textContent = (node as Text).textContent ?? '';
      if (textContent.trim().length === 0) continue;

      let p = parent;
      // Also check if p === rootNode
      while (p && !elements.has(p) && p !== rootNode) p = p.parentElement;
      if (p) {
        const arr = textByParent.get(p);
        if (arr) arr.push(textContent);
      }
    }
  }

  const parents = [rootNode, ...elementList];
  const childrenByParent = new Map<Element, Element[]>(parents.map((e) => [e, []]));

  // Build parent→children relationships (only among tracked elements)
  elementList.forEach((e) => {
    // Don't try to find parent if element is outside rootNode
    if (!rootNode.contains(e)) return;

    let p = e.parentElement;
    while (p && !elements.has(p) && p !== rootNode) p = p.parentElement;
    const parentKey = elements.has(p as Element) ? (p as Element) : rootNode;
    childrenByParent.get(parentKey)?.push(e);
  });

  const getRole = (el: Element) => {
    const explicit = el.getAttribute('role');
    if (explicit) return explicit;
    const tag = el.tagName.toLowerCase();
    if (/^h[1-6]$/.test(tag)) return 'heading';
    switch (tag) {
      case 'a':
        return 'link';
      case 'button':
        return 'button';
      case 'input':
        return 'input';
      case 'nav':
        return 'navigation';
      default:
        return tag;
    }
  };

  const shouldStopHere = (el: Element) => !!stopAtSelector && (el.matches?.(stopAtSelector) ?? false);

  const toChildren = (e: Element, depth: number): A11yNode[] => {
    const kids = childrenByParent.get(e) ?? [];
    const out: A11yNode[] = [];
    for (const c of kids) {
      const r = walkOver(c, depth + 1);
      if (!r) continue;
      if (Array.isArray(r)) out.push(...r);
      else out.push(r);
    }
    return out;
  };

  function walkOver(e: Element, depth: number): A11yNode | null | A11yNode[] {
    const atLimit = depth >= maxDepth;
    const stopHere = shouldStopHere(e);

    if (shouldExclude(e)) {
      if (excludeMode === 'subtree') {
        // drop node and its subtree
        return null;
      } else {
        // strip-node: drop the node but lift its (filtered) children
        if (stopHere || atLimit) return null; // nothing to lift if we can't descend
        return toChildren(e, depth); // flattened list of children
      }
    }

    const ariaEntries = e
      .getAttributeNames()
      .filter((a) => a.startsWith('aria-'))
      .map((a) => [a, e.getAttribute(a)] as [string, string | null]);

    const textArr = textByParent.get(e) ?? [];
    const href = e.getAttribute('href') || undefined;
    const src = e.getAttribute('src') || undefined;
    const testId = e.getAttribute('data-testid') || undefined;

    // Build with type safety
    return {
      tag: e.tagName.toLowerCase(),
      role: getRole(e),
      ...(testId ? { 'data-testid': testId } : {}),
      ...(ariaEntries.length ? { aria: ariaEntries } : {}),
      ...(textArr.length ? { text: textArr } : {}),
      ...(href ? { href } : {}),
      ...(src ? { src } : {}),
      ...(!atLimit && !stopHere ? { children: toChildren(e, depth) } : {})
    };
  }

  // Return a stable DOC root to keep single-root shape
  const result = walkOver(rootNode, 0);
  if (Array.isArray(result)) {
    return { tag: 'DOC', children: result };
  }
  if (result == null) {
    // If root was excluded (subtree), return empty DOC
    return { tag: 'DOC', children: [] };
  }
  return result;
}
